import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, Play, LogOut } from 'lucide-react';
import Logo from '@/icons/Logo';
import { supabase } from '@/integrations/supabase/client';
import FreeSurveyDialog from './FreeSurveyDialog';
import AssessmentSelectorDialog from './AssessmentSelectorDialog';
import MyAssessmentsSection from './MyAssessmentsSection';


interface PremiumDashboardProps {
  onStartAssessment: (assessmentId: string, slug: string) => void;
  onContinueAssessment: (assessmentId: string, slug: string, attemptId?: string) => void;
  onViewResults: (assessmentId: string, slug: string, responseId?: string) => void;
  onLogout: () => void;
}

export default function PremiumDashboard({ onStartAssessment, onContinueAssessment, onViewResults, onLogout }: PremiumDashboardProps) {
  const [userName, setUserName] = useState('');
  const [showFreeSurveys, setShowFreeSurveys] = useState(false);
  const [showAssessmentSelector, setShowAssessmentSelector] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const name = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
        setUserName(name);
      }
    };
    fetchUser();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    onLogout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-8 w-8" />
            <span className="font-semibold text-foreground">Maturity Compass</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground">
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Greeting */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-foreground">
            Hello, <span className="text-primary">{userName}</span> 👋
          </h1>
          <p className="text-muted-foreground mt-2">Welcome to your full assessment dashboard.</p>
        </div>

        {/* My Assessments Section */}
        <MyAssessmentsSection
          onContinueAssessment={onContinueAssessment}
          onViewResults={onViewResults}
        />

        {/* Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Submitted Free Survey Tile */}
          <Card 
            className="group cursor-pointer border-2 border-border hover:border-primary/40 transition-all duration-300 hover:shadow-[var(--shadow-elegant)]"
            onClick={() => setShowFreeSurveys(true)}
          >
            <CardContent className="p-8 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-primary/10 text-primary group-hover:scale-110 transition-transform">
                <ClipboardList className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Submitted Free Survey</h2>
                <p className="text-sm text-muted-foreground">
                  View your previously submitted free survey responses, summary and recommendations.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Start Full Assessment Tile */}
          <Card 
            className="group cursor-pointer border-2 border-border hover:border-primary/40 transition-all duration-300 hover:shadow-[var(--shadow-elegant)]"
            onClick={() => setShowAssessmentSelector(true)}
          >
            <CardContent className="p-8 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-primary/10 text-primary group-hover:scale-110 transition-transform">
                <Play className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Start Full Assessment</h2>
                <p className="text-sm text-muted-foreground">
                  Take the full assessment with advanced questions and detailed analysis.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <FreeSurveyDialog 
        open={showFreeSurveys} 
        onOpenChange={setShowFreeSurveys} 
      />

      <AssessmentSelectorDialog
        open={showAssessmentSelector}
        onOpenChange={setShowAssessmentSelector}
        onSelect={onStartAssessment}
      />
    </div>
  );
}
